import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Home, Upload, LogOut, TrendingUp, Users, Calendar, BookOpen, History, Compass } from 'lucide-react';
import { motion } from 'framer-motion';
import ChangePasswordDialog from './ChangePasswordDialog';
import GenerationNotifier from './GenerationNotifier';
import StudentNotificationBell from './StudentNotificationBell';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-lg bg-white/90">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 py-3">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center"
              >
                <TrendingUp className="w-6 h-6 text-white" />
              </motion.div>
              <span className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                WISE
              </span>
            </Link>

            <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/') ? 'default' : 'ghost'}
                    className={isActive('/') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-dashboard-button"
                  >
                    <Link to="/">
                      <Home className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/upload') ? 'default' : 'ghost'}
                    className={isActive('/upload') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-upload-button"
                  >
                    <Link to="/upload">
                      <Upload className="w-4 h-4 mr-2" />
                      Analyze Offline Test
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/practice') ? 'default' : 'ghost'}
                    className={isActive('/practice') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-practice-button"
                  >
                    <Link to="/practice">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Practice Test
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/practice/history') ? 'default' : 'ghost'}
                    className={isActive('/practice/history') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-practice-history-button"
                  >
                    <Link to="/practice/history">
                      <History className="w-4 h-4 mr-2" />
                      Practice History
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/teachers') ? 'default' : 'ghost'}
                    className={isActive('/teachers') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-teachers-button"
                  >
                    <Link to="/teachers">
                      <Users className="w-4 h-4 mr-2" />
                      Book Session
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/discover-sessions') ? 'default' : 'ghost'}
                    className={isActive('/discover-sessions') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-discover-sessions-button"
                  >
                    <Link to="/discover-sessions">
                      <Compass className="w-4 h-4 mr-2" />
                      Discover Sessions
                    </Link>
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    asChild
                    size="sm"
                    variant={isActive('/my-bookings') ? 'default' : 'ghost'}
                    className={isActive('/my-bookings') ? 'bg-violet-600 text-white hover:bg-violet-700' : ''}
                    data-testid="nav-bookings-button"
                  >
                    <Link to="/my-bookings">
                      <Calendar className="w-4 h-4 mr-2" />
                      My Bookings
                    </Link>
                  </Button>
                </motion.div>
              </div>

              <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3 sm:pt-0 sm:border-t-0 sm:border-l sm:pl-4 sm:ml-2">
                <div className="text-right hidden md:block" data-testid="user-info">
                  <div className="text-sm font-medium text-slate-900" data-testid="user-name">
                    {user?.name}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="user-email">
                    <span>{user?.email}</span>
                    {user?.role === 'admin' && (
                      <span
                        className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs font-medium"
                        data-testid="user-role-badge"
                      >
                        Admin
                      </span>
                    )}
                  </div>
                </div>

                {user?.role === 'admin' && (
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                      data-testid="admin-panel-button"
                    >
                      <Link to="/admin">Admin Panel</Link>
                    </Button>
                  </motion.div>
                )}

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <StudentNotificationBell />
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ChangePasswordDialog />
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={logout}
                    data-testid="logout-button"
                    className="rounded-full"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="ml-1 hidden lg:inline">Logout</span>
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <GenerationNotifier />

      <main className="py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;